import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Table,
  Badge,
  Text,
  Group,
  ActionIcon,
  Select,
  Stack,
  Card,
  Loader,
  Alert,
  Button,
} from '@mantine/core';
import { IconEye, IconRefresh } from '@tabler/icons-react';
import { getAllOrdersAdmin } from '@/lib/admin/orders';
import { Order } from '@/types';
import { notifications } from '@mantine/notifications';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAuth } from '@/context/AuthContext';

const STATUS_COLORS: Record<Order['status'], string> = {
  pending: 'yellow',
  processing: 'blue',
  shipped: 'cyan',
  delivered: 'green',
};

const PAYMENT_STATUS_COLORS: Record<Order['payment']['status'], string> = {
  pending: 'yellow',
  succeeded: 'green',
  failed: 'red',
};

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin: isAdminUser, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || 'all'
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || !isAdminUser)) {
      navigate('/xyz/login');
      return;
    }

    if (user && isAdminUser) {
      loadOrders();
    }
  }, [user, isAdminUser, authLoading, navigate, statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedOrders = await getAllOrdersAdmin({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        limit: 100,
        orderBy: 'createdAt',
        orderDirection: 'desc',
      });
      setOrders(fetchedOrders);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to load orders',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      const d = typeof date === 'string' ? new Date(date) : date.toDate?.() || date;
      return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch {
      return 'Invalid date';
    }
  };

  const getTotalItems = (order: Order) => {
    return order.items.reduce((sum, item) => sum + item.quantity, 0);
  };

  if (authLoading || (loading && orders.length === 0)) {
    return (
      <AdminLayout>
        <Container size="xl" py="xl">
          <Loader size="lg" style={{ display: 'block', margin: '0 auto' }} />
        </Container>
      </AdminLayout>
    );
  }

  if (!user || !isAdminUser) {
    return null;
  }

  return (
    <AdminLayout>
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={1}>Orders</Title>
            <Group>
              <Select
                value={statusFilter}
                onChange={(value) => setStatusFilter(value || 'all')}
                data={[
                  { value: 'all', label: 'All Orders' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'shipped', label: 'Shipped' },
                  { value: 'delivered', label: 'Delivered' },
                ]}
                style={{ width: 200 }}
              />
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="light"
                onClick={loadOrders}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          {orders.length === 0 ? (
            <Card withBorder padding="xl" radius="md" style={{ textAlign: 'center' }}>
              <Text c="dimmed">No orders found</Text>
            </Card>
          ) : (
            <Card withBorder padding="md" radius="md">
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Order ID</Table.Th>
                    <Table.Th>Customer</Table.Th>
                    <Table.Th>Items</Table.Th>
                    <Table.Th>Total</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Payment</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr key={order.id}>
                      <Table.Td>
                        <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                          {order.id?.substring(0, 8)}...
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{order.customer.name}</Text>
                          <Text size="xs" c="dimmed">{order.customer.email}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{getTotalItems(order)} item(s)</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {order.total.toFixed(2)} PLN
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLORS[order.status]} variant="light">
                          {order.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={PAYMENT_STATUS_COLORS[order.payment.status]} variant="light" size="sm">
                          {order.payment.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{formatDate(order.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => navigate(`/xyz/orders/${order.id}`)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Stack>
      </Container>
    </AdminLayout>
  );
}
